import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useEffect, useState } from "react";
import { Droplets, Phone } from "lucide-react";
import { CONTACT, scrollToId } from "@/lib/api";
import { Mascot, MASCOT_INTERVAL } from "@/components/landing/Mascot";
import { Marquee } from "@/components/landing/Marquee";
import { useAvailability } from "@/hooks/useAvailability";

const Line = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const NextSlotPill = ({ data }) =>
  data?.next_available ? (
    <motion.button
      data-testid="hero-next-slot-pill"
      onClick={() => scrollToId("book")}
      animate={{
        scale: [1, 1.07, 1],
        boxShadow: [
          "0 0 0 0 rgba(173,255,1,0.6)",
          "0 0 0 12px rgba(173,255,1,0)",
          "0 0 0 0 rgba(173,255,1,0)",
        ],
      }}
      transition={{ duration: 1.7, repeat: Infinity, ease: "easeOut" }}
      className="flex items-center gap-2 rounded-full bg-navy text-white text-sm font-bold px-5 py-3 hover:bg-navy-light transition-colors duration-300"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-tlime opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-tlime" />
      </span>
      Next available:
      <motion.span
        className="text-tlime font-extrabold uppercase"
        animate={{ scale: [1, 1.14, 1], opacity: [1, 0.55, 1] }}
        transition={{ duration: 1.7, repeat: Infinity, ease: "easeInOut" }}
      >
        {data.next_available.label}
      </motion.span>
    </motion.button>
  ) : null;

const PriceCard = () => (
  <div data-testid="hero-usp-price" className="rounded-3xl bg-tlime text-navy p-4 sm:p-5 shadow-xl shadow-tlime/30 rotate-2 hover:rotate-0 transition-transform duration-300">
    <p className="font-bebas text-2xl leading-none">WE SHOW THE PRICE</p>
    <p className="mt-1.5 text-[12px] sm:text-[13px] font-semibold text-navy/80">
      $360 first half-hour. Tool fee + GST included. Published in the open — a first for the leak game.
    </p>
  </div>
);

const TimeCard = () => (
  <div data-testid="hero-usp-time" className="rounded-3xl bg-tpink text-white p-4 sm:p-5 shadow-xl shadow-tpink/30 -rotate-2 hover:rotate-0 transition-transform duration-300">
    <p className="font-bebas text-2xl leading-none">YOU PICK THE ARRIVAL TIME</p>
    <p className="mt-1.5 text-[12px] sm:text-[13px] font-semibold text-white/90">
      Live weekday slots, 7AM–3PM. You choose. We rock up. Simple as.
    </p>
  </div>
);

const Ctas = ({ large = false }) => (
  <>
    <button
      data-testid="hero-book-button"
      onClick={() => scrollToId("book")}
      className={`rounded-full bg-tpink border-2 border-navy text-white text-center hover:bg-tpink-hover hover:-translate-y-1 hover:shadow-2xl hover:shadow-tpink/40 transition-[transform,box-shadow,background-color] duration-300 ${
        large ? "w-[260px] h-[84px] flex flex-col items-center justify-center" : "w-full px-2 py-2.5"
      }`}
    >
      <span className={`block font-bebas leading-none tracking-wide ${large ? "text-3xl" : "text-2xl"}`}>FIND MY LEAK</span>
      <span className={`block font-semibold text-white/85 leading-tight mt-1 ${large ? "text-xs" : "text-[11px]"}`}>See today's availability</span>
    </button>
    {large && (
      <a
        data-testid="hero-call-button"
        href={CONTACT.phoneHref}
        className="rounded-full bg-navy border-2 border-navy text-white font-bebas tracking-wide hover:bg-white hover:text-navy transition-[background-color,color] duration-300 w-[260px] h-[84px] flex items-center justify-center gap-2 text-3xl"
      >
        <Phone className="w-5 h-5" />
        {CONTACT.phone}
      </a>
    )}
    <button
      data-testid="hero-pricing-button"
      onClick={() => scrollToId("pricing")}
      className={`rounded-full bg-tlime border-2 border-navy text-navy text-center hover:-translate-y-1 hover:shadow-2xl hover:shadow-tlime/40 transition-[transform,box-shadow] duration-300 ${
        large ? "w-[260px] h-[84px] flex flex-col items-center justify-center" : "w-full px-2 py-2.5"
      }`}
    >
      <span className={`block font-bebas leading-none tracking-wide ${large ? "text-3xl" : "text-2xl"}`}>SEE PRICING</span>
      <span className={`block font-semibold text-navy/70 leading-tight mt-1 ${large ? "text-xs" : "text-[11px]"}`}>$360 1st half-hour++</span>
    </button>
  </>
);

const poof = {
  initial: { opacity: 0, scale: 0.4, filter: "blur(10px)" },
  animate: { opacity: 1, scale: 1, filter: "blur(0px)" },
  exit: { opacity: 0, scale: 1.6, filter: "blur(16px)" },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

export const Hero = () => {
  const { data } = useAvailability();
  const [frame, setFrame] = useState("left");
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  useEffect(() => {
    const t = setInterval(
      () => setFrame((f) => (f === "left" ? "right" : "left")),
      MASCOT_INTERVAL
    );
    return () => clearInterval(t);
  }, []);

  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const drop1x = useTransform(sx, (v) => v * 30);
  const drop1y = useTransform(sy, (v) => v * 20);
  const drop2x = useTransform(sx, (v) => v * -45);
  const drop2y = useTransform(sy, (v) => v * -30);

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative lg:min-h-screen hero-grid-bg noise-overlay overflow-hidden pt-32 sm:pt-28 lg:pt-0 pb-0 lg:pb-0"
    >
      <motion.div style={{ x: drop1x, y: drop1y }} className="absolute top-24 right-[8%] w-64 h-64 sm:w-96 sm:h-96 rounded-full bg-brand/25 blur-3xl" />
      <motion.div style={{ x: drop2x, y: drop2y }} className="absolute bottom-10 left-[4%] w-72 h-72 rounded-full bg-tpink/20 blur-3xl" />
      <motion.div style={{ x: drop2x, y: drop1y }} className="absolute top-1/3 left-[6%] hidden lg:block text-brand/20">
        <Droplets className="w-24 h-24" strokeWidth={1} />
      </motion.div>

      {/* ============ DESKTOP: mascot front & centre ============ */}
      <div className="hidden lg:block relative min-h-screen">
        <h1 className="absolute inset-0 z-10 font-bebas tracking-tight text-navy text-[10.5vw] leading-[0.85]">
          <span className="sr-only">Got a hidden leak? I got ya bruhs — Perth water & gas leak detection</span>
          <span className="absolute left-[3%] top-[42vh] -translate-y-1/2 block">
            <AnimatePresence>
              {frame === "left" && (
                <motion.span key="copy-left" {...poof} data-testid="hero-copy-left" className="block">
                  GOT A
                  <br />
                  HIDDEN
                  <br />
                  <span className="text-brand">LEAK?</span>
                </motion.span>
              )}
            </AnimatePresence>
          </span>
          <span className="absolute right-[3%] top-[42vh] -translate-y-1/2 block text-right">
            <AnimatePresence>
              {frame === "right" && (
                <motion.span key="copy-right" {...poof} data-testid="hero-copy-right" className="block">
                  I GOT YA
                  <br />
                  <span className="text-tpink">BRUHS!</span>
                </motion.span>
              )}
            </AnimatePresence>
          </span>
        </h1>

        <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-[5] h-[92vh] max-h-none aspect-square">
          <Mascot testId="mascot-desktop" frame={frame} className="absolute inset-0 drop-shadow-2xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="absolute left-[3.5%] bottom-[19vh] z-10 w-[21rem] xl:w-[23rem]"
        >
          <TimeCard />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="absolute right-[3.5%] bottom-[19vh] z-10 w-[21rem] xl:w-[23rem] space-y-4"
        >
          <PriceCard />
        </motion.div>

        <div className="absolute bottom-[4vh] left-1/2 -translate-x-1/2 z-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="flex items-center gap-5"
          >
            <Ctas large />
          </motion.div>
        </div>
      </div>

      {/* ============ MOBILE ============ */}
      <div className="lg:hidden relative z-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="flex flex-wrap items-center gap-3 mb-3 px-5 sm:px-8"
        >
          <span className="font-bold text-xs sm:text-sm tracking-[0.25em] text-brand uppercase">
            Perth · Water & Gas Leak Detection
          </span>
          <NextSlotPill data={data} />
        </motion.div>

        <div className="relative w-full max-w-[540px] mx-auto aspect-square">
          <Mascot testId="mascot-mobile" frame={frame} className="absolute inset-0" />
          <h1 className="absolute inset-0 z-10 font-bebas tracking-tight text-navy text-[14vw] sm:text-[11vw] leading-[0.85]">
            <span className="sr-only">Got a hidden leak? I got ya bruhs — Perth water & gas leak detection</span>
            <span className="absolute left-1 top-[38%] -translate-y-1/2 block">
              <AnimatePresence>
                {frame === "left" && (
                  <motion.span key="m-copy-left" {...poof} data-testid="hero-copy-left-mobile" className="block text-outline-white">
                    GOT A
                    <br />
                    HIDDEN
                    <br />
                    <span className="text-brand">LEAK?</span>
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
            <span className="absolute right-1 top-[38%] -translate-y-1/2 block text-right">
              <AnimatePresence>
                {frame === "right" && (
                  <motion.span key="m-copy-right" {...poof} data-testid="hero-copy-right-mobile" className="block text-outline-white">
                    I GOT
                    <br />
                    YA
                    <br />
                    <span className="text-tpink">BRUHS!</span>
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
          </h1>
        </div>

        <div className="relative z-20 -mt-2.5">
          <Marquee />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="relative z-20 max-w-[540px] mx-auto grid grid-cols-2 gap-3 px-4 mt-4 pb-4"
        >
          <Ctas />
        </motion.div>

      </div>
    </section>
  );
};

export const HeroExtras = () => (
  <div className="lg:hidden bg-white px-5 sm:px-8 py-8">
    <p className="max-w-xl text-sm sm:text-lg text-navy/70 font-medium">
      Acoustic ears. Thermal eyes. Tracer gas. We find your leak and fix it properly —
      with the price online and the rock-up time chosen by you. No waffle. No surprises.
    </p>
    <div className="mt-5 grid sm:grid-cols-2 gap-3 sm:gap-4 max-w-3xl">
      <TimeCard />
      <PriceCard />
    </div>
  </div>
);
